import React, {
    Fragment,
    useEffect,
    useState,
    useMemo,
    useCallback
} from 'react';
import { useStyle } from '../lib/util/classify';
import defaultClasses from './configuratorSection.module.css';
import Dialog from '../components/Dialog';
import RichContent from '../components/RichContent';
import TextInput from '../components/TextInput';
import ColorList from './colorList';
import MontageItems from './montageItems';
import CaseTypes from './caseTypes';
import ControlTypes from './controlTypes';
import ProductOptions from '../components/LegacyMiniCart/productOptions';
import { useUserContext } from '@magento/peregrine/lib/context/user';
import ConfiguratorShare from './configuratorShare';
import ConfiguratorWizard from './configuratorWizard';
import { Tool as ToolIcon, Compass as CompassIcon } from 'react-feather';
import { isProductRenderable } from '../peregrine/lib/util/isProductConfigurator';

const convertToMillimeter = (value, unit) => {
    if (unit.toLowerCase() == 'mm') {
        return value;
    }
    switch (unit.toLowerCase()) {
        case 'cm':
            return value * 10;
        case 'm':
            return value * 1000;
        default:
            console.error('ConvertToMillimeter unknown unit:' + unit);
            return null;
    }
};

const DEFAULT_CONTROL_POSITIONS = [
    {
        label: _('Izquierda'),
        value: 'left',
        key: 'left',
        code: 'left',
        group: 'default'
    },
    {
        label: _('Derecha'),
        value: 'right',
        key: 'right',
        code: 'right',
        group: 'default'
    }
];

const STEPS_LENGTH_ONLY = [
    {
        index: 0,
        code: 'dimensions',
        label: _('Dimensions')
    },
    {
        index: 1,
        code: 'color',
        label: _('Color select')
    }
];

const STEPS_CONFIGURATOR = [
    {
        index: 0,
        code: 'dimensions',
        label: _('Dimensions')
    },
    {
        index: 1,
        code: 'montage',
        label: _('Montage')
    },
    {
        index: 2,
        code: 'case',
        label: _('Case select')
    },
    {
        index: 3,
        code: 'color',
        label: _('Color select')
    },
    {
        index: 4,
        code: 'control',
        label: _('Control select')
    }
];

export const stripHtml = html => html.replace(/(<([^>]+)>)/gi, '');

// converts the provided human-friendly configuration to an array of {option_label, value_label}
// that is required by the component ProductOptions
export const configuratorOptionsToProductOptions = options =>
    (options || '')
        .replace(/##.*?##$/, '')
        .trim()
        .split('\n')
        .filter(line => line.length)
        .map(line => {
            // label is everything up to the first ":", the rest is the value
            const parts = line.split(':');
            return {
                option_label: parts.shift(),
                value_label: parts.join(':')
            };
        });

const getMontagesSelected = montageItems => {
    const montagesSelected = new Map();

    if (Array.isArray(montageItems)) {
        montageItems.forEach(montageItem => {
            // "default" is a reserved word, so we rename it to itemDefault
            const { key, default: itemDefault = null } = montageItem;
            // selected item is the code indicated in the property "default"
            // if none is default, we use the code of the first item
            montagesSelected.set(key, itemDefault || montageItem.items[0].key);
        });
    } else {
        Object.keys(montageItems).forEach(key => {
            montagesSelected.set(key, montageItems[key].items[0].code);
        });
    }

    return montagesSelected;
};

const mapToString = (
    map,
    sourceList,
    glue = ': ',
    separator = ', ',
    extrakey
) => {
    try {
        return Array.from(map)
            .map(item => {
                const tmp = Array.isArray(sourceList)
                    ? sourceList.find(
                          source => (source.code || source.key) == item[0]
                      )
                    : sourceList[item[0]];
                const label = tmp.label || item[0];

                const value =
                    (typeof item[1] == 'object'
                        ? `${item[1].label} ${item[1].code}`
                        : (tmp.items[extrakey] || tmp.items).find(
                              source => (source.key || source.code) == item[1]
                          ).label) +
                        '{{' +
                        (typeof item[1] == 'object'
                            ? item[1].techlabel
                            : (tmp.items[extrakey] || tmp.items).find(
                                  source =>
                                      (source.key || source.code) == item[1]
                              ).techlabel) +
                        '}}' || item[1];

                //                return `${label}${glue}"${value}" (${item[1]})`;
                return `${label}${glue}"${value}"`.replace(/::/, ':');
            })
            .join(separator);
    } catch (e) {
        console.error(e);
        //        debugger;
    }
};

/**
 * ConfiguratorSection
 *
 * @typedef ConfiguratorSection
 * @kind functional component
 *
 * @param {props} props React component props
 *
 * @returns {React.Element} A React component that displays a configurator
 */
const ConfiguratorSection = props => {
    const {
        product,
        onPriceChange,
        onAdvicedPriceChange,
        priceBlock,
        addToCartBlock,
        setIsConfiguratorValid,
        onConfiguredColorChange,
        onConfiguredCaseChange,
        onConfiguredCaseModelChange,
        onConfiguredWidthChange,
        onConfiguredHeightChange
    } = props;

    // Debug: Log product structure
    console.log('ConfiguratorSection received product:', product);

    if (!product) {
        console.error('ConfiguratorSection: product is undefined');
        return null;
    }

    const { configurator_data, prices: baseProductPrices = [] } = product;

    const configurator = useMemo(() => {
        if (!configurator_data) {
            console.error('configurator_data is missing from product');
            return null;
        }
        try {
            const configurator = JSON.parse(configurator_data);
            console.log('CONFIGURATOR', configurator);
            return configurator;
        } catch (e) {
            console.error('Failed to parse configurator_data:', e);
            return null;
        }
    }, [configurator_data]);

    if (!configurator) {
        return <div>Error: Invalid configurator data</div>;
    }

    const classes = useStyle(defaultClasses, props.classes);

    const [{ isSignedIn, currentUser }] = useUserContext();
    const customerGroupId = isSignedIn ? currentUser.group_id : 0;

    const productDiscount = useMemo(() => {
        try {
            if (!baseProductPrices || !baseProductPrices.length) {
                return null;
            }
            const priceForGroup = baseProductPrices.find(
                prices => prices.customer_group_id == customerGroupId
            );
            if (!priceForGroup || !priceForGroup.price_tiers) {
                return null;
            }
            const discountTier = priceForGroup.price_tiers.find(
                price => price.discount.__typename == 'ProductDiscount'
            );
            const discount = discountTier ? discountTier.discount : null;

            return discount ? discount.percent_off : null;
        } catch (e) {
            return null;
        }
    }, [baseProductPrices, customerGroupId]);

    const urlParams = new URL(document.location.href).searchParams;

    //    console.log('baseProductPrices', baseProductPrices, customerGroupId, productDiscount);

    const {
        colors: colorLists = {},
        colors_ral = [],
        montageItems = [],
        kastModels: caseModels = [],
        kastTypes: caseTypes = [],
        bedieningszijde: controlPositions = DEFAULT_CONTROL_POSITIONS,
        controlTypes = [],
        buying,
        lengthonly,
        prices: pricesMatrix = [],
        nm: nmMatrix,
        help = {},
        extraCart = '',
        minsize,
        maxsize,
        //        minwidth = 100,
        //        minheight = 100,
        //        maxwidth = 300,
        //        maxheight = 300,
        maten = {}
    } = configurator;

    const steps = [
        ...(configurator.lengthonly ? STEPS_LENGTH_ONLY : STEPS_CONFIGURATOR),
        {
            index: 5,
            code: 'summary',
            label: _('Summary')
        }
    ];

    const [activeStep, setActiveStep] = useState(steps[0]);
    const [nextStep, setNextStep] = useState(steps[1]);
    const [prevStep, setPrevStep] = useState();

    const goToNextStep = useCallback(() => {
        if (nextStep) {
            setActiveStep(nextStep);
        }
    }, [nextStep]);

    const goToPrevStep = useCallback(() => {
        if (prevStep) {
            setActiveStep(prevStep);
        }
    }, [prevStep]);

    useEffect(() => {
        const currentIndex = activeStep.index;
        setPrevStep(steps[currentIndex - 1] || null);
        setNextStep(steps[currentIndex + 1] || null);
    }, [activeStep]);

    // if there's a control side from the url, we need to unset the existing default control side (if any)
    if (urlParams.get('controlSide')) {
        const defaultControlPosition = controlPositions.find(
            item => item.selected && item.key != urlParams.get('controlSide')
        );

        if (defaultControlPosition) {
            defaultControlPosition.selected = false;
        }

        controlPositions.find(
            ({ key }) => key == urlParams.get('controlSide')
        ).selected = true;
    }

    console.log('control side', controlPositions, controlTypes);

    const useMatrixPrice = !!(pricesMatrix && Object.keys(pricesMatrix).length);

    const {
        maat_breedte: sizeWidth = {},
        maat_hoogte: sizeHeight = {}
    } = maten;

    if (!sizeWidth.units) {
        sizeWidth.units = 'cm';
    }

    if (!sizeHeight.units) {
        sizeHeight.units = 'cm';
    }

    // @TODO: default hardcoded values for defaultWidth and defaultHeight
    const defaultWidth = 100;
    const defaultHeight = 100;

    const [isLoaded, setIsLoaded] = useState(false);

    const hasCaseTypes = caseTypes && caseTypes.length;

    const [printWizardElements, setPrintWizardElements] = useState(
        (!lengthonly && hasCaseTypes) || Object.keys(caseTypes).length
    );

    const [useWizard, setUseWizard] = useState(
        !printWizardElements
            ? false
            : window.localStorage && localStorage.getItem('useWizard') === null
            ? true
            : !!parseInt(localStorage.getItem('useWizard'))
    );

    useEffect(() => {
        console.log('usewizard', useWizard);
        window.localStorage &&
            window.localStorage.setItem('useWizard', useWizard ? 1 : 0);
    }, [useWizard]);

    if (!caseModels.items) {
        caseModels.items = [];
    }

    const defaultModel =
        urlParams.get('caseModel') || (caseModels && caseModels.default);
    const [caseModel, setCaseModel] = useState(defaultModel);
    const [caseTypeItems, setCaseTypeItems] = useState(
        defaultModel ? caseTypes[caseModel] : caseTypes
    );

    const [sizeWidthMin, setSizeWidthMin] = useState(
        (sizeWidth && sizeWidth.min) || null
    );

    const [sizeWidthMax, setSizeWidthMax] = useState(
        (sizeWidth && sizeWidth.max) || null
    );

    const [sizeWidthMaxRange, setSizeWidthMaxRange] = useState(sizeWidthMax);

    const [sizeHeightMax, setSizeHeightMax] = useState(
        (sizeHeight && sizeHeight.max) || null
    );
    const [sizeHeightMin, setSizeHeightMin] = useState(
        (sizeHeight && sizeHeight.min) || null
    );

    const [sizeHeightMaxRange, setSizeHeightMaxRange] = useState(sizeHeightMax);

    const [caseHeight, setCaseHeight] = useState();

    const [autoSelectedMotor, setAutoSelectedMotor] = useState();

    useEffect(() => {
        if (caseModels.items && caseModels.items.length > 1) {
            const sortedCaseModels = caseModels.items.sort(
                (a, b) => a.max_width - b.max_width
            );
            const minWidth = sortedCaseModels[0].max_width;
            const maxWidth =
                sortedCaseModels[sortedCaseModels.length - 1].max_width;
            setSizeWidthMax(maxWidth);

            const minHeight = sortedCaseModels[0].max_height;
            const maxHeight =
                sortedCaseModels[sortedCaseModels.length - 1].max_height;
            setSizeHeightMax(maxHeight);

            if (minWidth == maxWidth) {
                setSizeWidthMaxRange(minWidth);
            } else {
                setSizeWidthMaxRange(_('From {0} to {1}', minWidth, maxWidth));
            }

            if (minHeight == maxHeight) {
                setSizeHeightMaxRange(minHeight);
            } else {
                setSizeHeightMaxRange(
                    _('From {0} to {1}', minHeight, maxHeight)
                );
            }
        } else {
            setSizeWidthMax(sizeWidthMax);
            setSizeWidthMaxRange(sizeWidthMax);
            setSizeHeightMax(sizeHeightMax);
            setSizeHeightMaxRange(sizeHeightMax);
        }
    }, [caseModels]);

    useEffect(() => {
        if (productDiscount) {
            if (montageItems) {
                montageItems.forEach(montageItem =>
                    montageItem.items.map(
                        item =>
                            (item.displayPrice =
                                item.price -
                                item.price / (100 / productDiscount))
                    )
                );
            }
            if (controlTypes) {
                controlTypes.map(controlType => {
                    if (controlType.price) {
                        controlType.displayPrice =
                            controlType.price -
                            controlType.price / (100 / productDiscount);
                    }

                    if (controlType.items && controlType.items.length) {
                        controlType.items.map(
                            item =>
                                (item.displayPrice =
                                    item.price -
                                    item.price / (100 / productDiscount))
                        );
                    }
                });
            }
        } else {
            if (montageItems) {
                montageItems.forEach(options =>
                    options.items.forEach(item => {
                        delete item.displayPrice;
                    })
                );
            }
            if (controlTypes) {
                controlTypes.forEach(controlType => {
                    delete controlType.displayPrice;
                });
            }
        }
    }, [productDiscount, montageItems, controlTypes]);

    useEffect(() => {
        if (lengthonly) {
            return;
        }

        if (caseModel) {
            setCaseTypeItems(caseTypes[caseModel]);

            const selectedCaseModel = caseModels.items.find(
                model => model.code == caseModel
            );

            console.log(
                'SELECTED CASE MODEL',
                selectedCaseModel,
                sizeWidthMin,
                sizeWidthMax,
                sizeHeightMin,
                sizeHeightMax
            );

            if (selectedCaseModel.min_width) {
                setSizeWidthMin(selectedCaseModel.min_width);
            }

            if (selectedCaseModel.max_width) {
                //setSizeWidthMax(selectedCaseModel.max_width);
            }

            if (selectedCaseModel.min_height) {
                setSizeHeightMin(selectedCaseModel.min_height);
            }

            if (selectedCaseModel.max_height) {
                // setSizeHeightMax(selectedCaseModel.max_height);
            }

            setCaseHeight(selectedCaseModel.value / 10);

            onConfiguredCaseModelChange(selectedCaseModel);
        } else {
            const caseHeightMm =
                convertToMillimeter(caseHeight, sizeHeight.units) || undefined;

            // fake caseModel object
            onConfiguredCaseModelChange({
                value: caseHeightMm
            });
        }
    }, [lengthonly, caseModel, caseHeight, sizeHeight]);

    useEffect(() => {
        const min = document.querySelector('[name="min"]');
        if (min) {
            min.step = (sizeWidth && sizeWidth.step) || 0.1;
        }

        const max = document.querySelector('[name="max"]');
        if (max) {
            max.step = (sizeHeight && sizeHeight.step) || 0.1;
        }
    }, [sizeWidth, sizeHeight]);

    const defaultCaseType =
        caseTypeItems && caseTypeItems[0] && caseTypeItems[0].code;
    const [caseType, setCaseType] = useState(
        urlParams.get('caseType') || defaultCaseType
    );

    // create a map with the selected colors, which for now defaults to the first color
    const colorsSelected = new Map();
    Object.keys(colorLists).forEach(key => {
        try {
            let defaultItem;

            const items =
                colorLists[key].items[caseType && 'kast_type-' + caseType] ||
                colorLists[key].items;

            if (colorLists[key].default) {
                if (colorLists[key].default instanceof Object) {
                    defaultItem =
                        colorLists[key].default['kast_type-' + caseType];
                } else {
                    // string
                    defaultItem = colorLists[key].default;
                }

                if (defaultItem && items instanceof Array) {
                    const defaultSelectedItem = items.find(
                        item => item.code == defaultItem
                    );

                    colorsSelected.set(
                        key,
                        defaultSelectedItem && defaultSelectedItem.code
                    );
                }
            } else {
                // no default item
            }
        } catch (e) {
            console.error(e);
        }
    });

    console.log('COLORS SELECTED INITIAL', colorsSelected);

    // find the default control type, falling back to the first product
    const controlTypeSelected = urlParams.get('controlType')
        ? controlTypes.find(({ key }) => key == urlParams.get('controlType'))
        : controlTypes.find(item => item.default) || controlTypes[0];
    const controlProductSelected =
        controlTypeSelected && controlTypeSelected.items
            ? (
                  controlTypeSelected.items.find(
                      product => product.key == urlParams.get('controlProduct')
                  ) ||
                  controlTypeSelected.items.find(product => product.default) ||
                  controlTypeSelected.items[0]
              ).value
            : null;

    useEffect(() => {
        if (!controlTypeSelected) {
            return;
        }

        const selector = `[for="radio-controlType-${controlTypeSelected.key}"]`;
        document.querySelector(selector).click();

        let interval;

        if (controlProductSelected) {
            interval = setInterval(() => {
                const selector = `[for="radio-controlProduct-${controlProductSelected}"]`;
                const element = document.querySelector(selector);

                if (element) {
                    element.click();
                    clearInterval(interval);
                }
            }, 50);
        }

        return () => {
            clearInterval(interval);
        };
    }, []);

    // @TODO: default values
    const [width, setWidth] = useState(urlParams.get('width') || sizeWidthMin);
    const [height, setHeight] = useState(
        urlParams.get('height') || sizeHeightMin
    );

    const [controlSide, setControlSide] = useState(
        urlParams.get('controlSide') || null
    );
    const [controlType, setControlType] = useState(
        controlTypeSelected ? controlTypeSelected.value : ''
    );
    const [controlProduct, setControlProduct] = useState(
        controlProductSelected
    );

    const [configuratorOptions, setConfiguratorOptions] = useState();
    const [montages, setMontages] = useState(
        (JSON.parse(urlParams.get('montages')) &&
            new Map(JSON.parse(urlParams.get('montages')))) ||
            getMontagesSelected(montageItems)
    );

    const [colors, setColors] = useState(
        (JSON.parse(urlParams.get('colors')) &&
            new Map(JSON.parse(urlParams.get('colors')))) ||
            colorsSelected
    );

    const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

    // one-time-run-only function to set parameters from the url
    useEffect(() => {
        if (urlParamsProcessed) {
            return;
        }
        const montages =
            JSON.parse(urlParams.get('montages')) &&
            new Map(JSON.parse(urlParams.get('montages')));

        if (montages && montages.size) {
            montages.forEach((value, key) => {
                const selector = `[for="radio-${key}-${value}"]`;
                document.querySelector(selector).click();
            });
        }

        const colors =
            JSON.parse(urlParams.get('colors')) &&
            new Map(JSON.parse(urlParams.get('colors')));

        if (colors && colors.size) {
            colors.forEach((value, key) => {
                const selector = `[for="radio-${key}-${value}"]`;
                document.querySelector(selector).click();
            });
        }

        setUrlParamsProcessed(true);
    }, [urlParams]);

    const [guiderLeftSize, setGuiderLeftSize] = useState();
    const [guiderRightSize, setGuiderRightSize] = useState();

    useEffect(() => {
        if (!montageItems || !montageItems.length) {
            return;
        }

        try {
            const selectedLeftItemCode = montages.get('type_geleider_links');
            const leftCategory = montageItems.find(item => item.key == 'type_geleider_links');
            const selectedLeftItem = leftCategory && leftCategory.items
                ? leftCategory.items.find(item => item.code == selectedLeftItemCode)
                : null;

            setGuiderLeftSize(
                selectedLeftItem
                    ? Number(selectedLeftItem.size).toLocaleString()
                    : null
            );

            const selectedRightItemCode = montages.get('type_geleider_rechts');
            const rightCategory = montageItems.find(item => item.key == 'type_geleider_rechts');
            const selectedRightItem = rightCategory && rightCategory.items
                ? rightCategory.items.find(item => item.code == selectedRightItemCode)
                : null;

            setGuiderRightSize(
                selectedRightItem
                    ? Number(selectedRightItem.size).toLocaleString()
                    : null
            );
        } catch (e) {}
    }, [montages]);

    const setSelectedColor = (colorCode, key) => {
        colors.set(key, colorCode);
        setColors(new Map([...colors]));
        console.log('XXX Selected colors', key, colorCode, colors);
    };

    const prevRenderingColorsRef = React.useRef(null);
    
    useEffect(() => {
        if (!isProductRenderable(product)) {
            return;
        }

        console.log('XXX colors', colors, colorLists);

        const defaultCaseColor =
            colorLists.kast_geleiders && colorLists.kast_geleiders.default
                ? colorLists.kast_geleiders.default[`kast_type-${caseType}`]
                : null;

        let colorKastGeleiders;

        if (typeof colors.get('kast_geleiders') == 'object') {
            colorKastGeleiders = colors.get('kast_geleiders');
        } else if (
            colorLists.kast_geleiders &&
            colorLists.kast_geleiders.items &&
            colorLists.kast_geleiders.items['kast_type-' + caseType]
        ) {
            const caseTypeColors =
                colorLists.kast_geleiders.items['kast_type-' + caseType];
            colorKastGeleiders =
                caseTypeColors.find(
                    item => item.code == colors.get('kast_geleiders')
                ) ||
                caseTypeColors.find(item => item.code == defaultCaseColor);
        } else {
            colorKastGeleiders = null;
        }

        let colorBlinds;

        if (
            typeof colors.get('lamellen') == 'object' ||
            typeof colors.get('kleur_doek') == 'object'
        ) {
            colorBlinds = colors.get('lamellen') || colors.get('kleur_doek');
        } else {
            const blindColorList = colorLists.lamellen || colorLists.kleur_doek;
            colorBlinds =
                blindColorList && blindColorList.items
                    ? blindColorList.items.find(
                          item =>
                              item.code ==
                              (colors.get('lamellen') || colors.get('kleur_doek'))
                      )
                    : null;
        }

        // Only call callback if values actually changed (prevent infinite loop)
        const prev = prevRenderingColorsRef.current;
        const caseChanged = prev?.get('case') !== colorKastGeleiders;
        const blindsChanged = prev?.get('blinds') !== colorBlinds;
        
        if (!prev || caseChanged || blindsChanged) {
            const renderingColors = new Map([
                ['case', colorKastGeleiders],
                ['blinds', colorBlinds]
            ]);

            console.log('XXX renderingColors', renderingColors);
            prevRenderingColorsRef.current = renderingColors;
            onConfiguredColorChange(renderingColors);
        }
    }, [colors, colorLists, caseType, lengthonly]);

    const prevCaseTypeRef = React.useRef(null);
    
    useEffect(() => {
        // Only call callback if value actually changed (prevent infinite loop)
        if (prevCaseTypeRef.current !== caseType) {
            prevCaseTypeRef.current = caseType;
            onConfiguredCaseChange(caseType);
        }
    }, [caseType]);

    const setSelectedMontage = (itemKey, key) => {
        montages.set(key, itemKey);
        // we need to return a new variable so React knows that the variable changed
        setMontages(new Map([...montages]));

        console.log('Selected montages', key, itemKey, montages, montageItems);
    };

    useEffect(() => {
        const lines = [];

        if (lengthonly) {
            lines.push([
                _('Length'),
                width,
                '%s ' +
                    sizeWidth.units +
                    '{{' +
                    convertToMillimeter(width, sizeWidth.units) +
                    'MM}}'
            ]);
        } else {
            lines.push(
                [
                    _('Anchura'),
                    width || defaultWidth,
                    '%s ' +
                        sizeWidth.units +
                        '{{' +
                        convertToMillimeter(
                            width || defaultWidth,
                            sizeWidth.units
                        ) +
                        'MM}}'
                ],
                [
                    _('Altura'),
                    height || defaultHeight,
                    '%s ' +
                        sizeHeight.units +
                        '{{' +
                        convertToMillimeter(
                            height || defaultHeight,
                            sizeHeight.units
                        ) +
                        'MM}}'
                ]
            );
        }
        // @TODO guiders : push label of the guider not the cm value.
        // @TODO if value is "empty" or geen than do not print tthe line. for example valse vesterbank : "geen"
        // @TODO if multiple values can be selected within one group the set a break rule in the text we print.
        // montaje : print without label to checkout. just the item label
        // general: The attribute yd_conf_kast_model is missing the obligatory definition [kast_models] : this specific input error needs to be hidden for yd_conf_kast_model
        // when a configator product is added to the cart and in the cart you select "edit qty" the cart throws error :: DEBUG TypeError9B at UseCartOptions

        try {
            const montage = mapToString(montages, montageItems);

            if (montage) {
                montage
                    .split(/\s*,\s*/)
                    .map(line => line.replace(/\s*\"/gims, '').split(':'))
                    .filter(
                        line => line[1] != 'geen' && line[1] != 'geen zender'
                    )
                    .forEach(line => lines.push([line[0], line[1], '%s']));
            }

            const colorsMap = mapToString(
                colors,
                colorLists,
                ': ',
                ', ',
                'kast_type-' + caseType
            );

            if (colorsMap) {
                colorsMap
                    .split(/\s*,\s*/)
                    .map(line => line.replace(/\s*\"/gims, '').split(':'))
                    .filter(
                        line => line[1] != 'geen' && line[1] != 'geen zender'
                    )
                    .forEach(line => lines.push([line[0], line[1], '%s']));
            }
            //            lines.push([
            //                _('Color'),
            //                mapToString(
            //                    colors,
            //                    colorLists,
            //                    ': ',
            //                    ', ',
            //                    'kast_type-' + caseType
            //                ),
            //                '%s'
            //            ]);

            //            lines.push([
            //                _('Montaje'),
            //                mapToString(montages, montageItems),
            //                '%s'
            //            ]);
            if (controlSide) {
                const selectedControlPosition = controlPositions.find(
                    item => item.key == controlSide
                );

                lines.push([
                    _('Lado de control'),
                    selectedControlPosition.label,
                    '%s{{' +
                        (selectedControlPosition.techlabel ||
                            selectedControlPosition.value) +
                        '}}'
                ]);
            }
        } catch (e) {
            console.error(e);
        }

        //        console.log('CONFIGURED', colors, colorLists, mapToString(colors, colorLists, ': ', ', ', 'kast_type-'+caseType));

        if (configurator.kase) {
            lines.push([
                _('Altura de cajón'),
                caseHeight,
                '%s ' +
                    sizeHeight.units +
                    '{{' +
                    convertToMillimeter(caseHeight, sizeHeight.units) +
                    'MM}}'
            ]);
        }

        const selectedCaseModel = caseModel
            ? caseModels.items.find(item => item.code == caseModel)
            : null;

        if (selectedCaseModel) {
            // print only the first line of descriptions like this:
            // '<b>85mm</b><br> max tot 300cm breed'
            const caseModelLabel = selectedCaseModel.label.split(
                /<br *\/?>/
            )[0];

            if (selectedCaseModel.techlabel) {
                lines.push([
                    _('Modelo de cajón'),
                    caseModelLabel + '{{' + selectedCaseModel.techlabel + '}}',
                    '%s'
                ]);
            } else {
                lines.push([_('Modelo de cajón'), caseModelLabel, '%s']);
            }
        }

        const selectedCaseType =
            caseTypeItems.find &&
            caseTypeItems.find(item => item.code == caseType);

        if (selectedCaseType) {
            if (selectedCaseType.techlabel) {
                lines.push([
                    _('Tipo de cajón'),
                    selectedCaseType.label +
                        '{{' +
                        selectedCaseType.techlabel +
                        '}}',
                    '%s'
                ]);
            } else {
                lines.push([_('Tipo de cajón'), selectedCaseType.label, '%s']);
            }
        }

        const selectedControlType = controlTypes.find(
            item => item.key == controlType
        );

        if (selectedControlType) {
            /*
            lines.push([_('Tipo de manejo'), selectedControlType.label, '%s']);

            const selectedControlProductItem = (
                controlTypes.find(item => item.key == controlType).items || []
            ).find(item => item.key == controlProduct);

            if (selectedControlProductItem) {
                lines.push([
                    _('Opciones de control'),
                    controlTypes
                        .find(item => item.key == controlType)
                        .items.find(item => item.key == controlProduct).label,
                    '%s'
                ]);
            }
            */
            let selectedControlLabel = selectedControlType.techlabel
                ? selectedControlType.label +
                  '{{' +
                  selectedControlType.techlabel +
                  '}}'
                : selectedControlType.label;

            const selectedControlProductItem = (
                selectedControlType.items || []
            ).find(item => item.key == controlProduct);

            if (
                selectedControlProductItem &&
                selectedControlProductItem.label.indexOf('geen') == -1
            ) {
                if (selectedControlProductItem.techlabel) {
                    selectedControlLabel +=
                        ': ' +
                        selectedControlProductItem.label +
                        '{{' +
                        selectedControlProductItem.techlabel +
                        '}}';
                } else {
                    selectedControlLabel +=
                        ': ' + selectedControlProductItem.label;
                }
            }

            lines.push([_('Tipo de manejo'), selectedControlLabel, '%s']);

            if (autoSelectedMotor) {
                const {
                    sku,
                    strength,
                    sunshade_sku,
                    techlabel
                } = autoSelectedMotor;
                // starting with two underscores will make the line not visible in frontend
                lines.push([
                    '__' + _('Selected motor'),
                    `${sku} ${strength} NM {{${techlabel ||
                        sunshade_sku ||
                        ''}}}`,
                    '%s'
                ]);
            }
        }

        const configured = lines
            .filter(item => !!item[1])
            .concat([[extraCart.trim(), '', '%s']])
            .map(item =>
                item[1]
                    ? `${item[0]}: ${stripHtml(
                          item[2].replace('%s', _(item[1]))
                      )}`
                    : item[0]
            )
            .join('\n')
            .trim();

        setConfiguratorOptions(configured);

        if (
            !product.options ||
            !product.options[0] ||
            !product.options[0].option_id
        ) {
            console.error(
                'THIS PRODUCT MISSES A TEXTAREA CUSTOM OPTION, IT CANNOT BE ADDED TO THE CART'
            );
        }
        
        // Note: customOptionId removed to prevent infinite re-render loop
        // Don't mutate product object directly

        const jsonConfig = Object.fromEntries(
            [...new URL(deepLink).searchParams].map(([key, values]) => {
                let parsed;
                try {
                    parsed = Object.fromEntries(JSON.parse(values));
                } catch (e) {
                    parsed = values;
                }
                return [key, parsed];
            })
        );

        jsonConfig.case_height = convertToMillimeter(
            caseHeight,
            sizeHeight.units
        );

        if (autoSelectedMotor) {
            const { sku } = autoSelectedMotor;
            jsonConfig.selected_motor = sku;
        }

        // Note: customOptionValue removed to prevent infinite re-render loop
        // Don't mutate product object directly
        const customOptionValue = configured + `\n##${JSON.stringify(jsonConfig)}##`;

        console.log('CONFIGURED', configured);
        console.log('CONFIGURED customOptionValue', customOptionValue);
    }, [
        width,
        height,
        colors,
        montages,
        caseHeight,
        caseModel,
        sizeHeight.units,
        caseType,
        controlType,
        controlSide,
        controlProduct,
        isLoaded,
        autoSelectedMotor
    ]);

    const prevWidthRef = React.useRef(null);
    const prevHeightRef = React.useRef(null);
    
    useEffect(() => {
        // Only call callback if value actually changed (prevent infinite loop)
        if (prevWidthRef.current !== width) {
            prevWidthRef.current = width;
            onConfiguredWidthChange(width);
        }
    }, [width]);

    useEffect(() => {
        // Only call callback if value actually changed (prevent infinite loop)
        if (prevHeightRef.current !== height) {
            prevHeightRef.current = height;
            onConfiguredHeightChange(height);
        }
    }, [height]);

    const cannotAddToCartAlert =
        !product.options ||
        !product.options[0] ||
        !product.options[0].option_id ? (
            <span style={{ color: 'red', fontWeight: 'bold' }}>
                Missing custom option TEXTAREA, PRODUCT cannot be ordered.
                Contact webmaster to complete te product
            </span>
        ) : null;

    const [isFormSubmited, setIsFormSubmited] = useState(false);

    window.setIsFormSubmited = setIsFormSubmited;

    const [totalSize, setTotalSize] = useState();

    useEffect(() => {
        setTotalSize(width * height);
    }, [width, height]);

    useEffect(() => {
        if (lengthonly || !nmMatrix) {
            return;
        }

        const minNm = getMatrixValue(nmMatrix, width, height);

        let autoSelectedMotor = null;

        console.log('min NM', minNm, controlType);

        controlTypes
            .filter(({ motors }) => motors && motors.length)
            .forEach(item => {
                const minMotor = item.motors
                    .sort((a, b) => a.strength - b.strength)
                    .find(item => item.strength >= minNm);

                console.log('min NM motor', minNm, item);

                if (minMotor) {
                    item.price = minMotor.price;

                    if (productDiscount) {
                        item.displayPrice =
                            item.price - (productDiscount * item.price) / 100;
                    } else {
                        item.displayPrice = item.price;
                    }

                    item.disabled = false;

                    if (!autoSelectedMotor && item.key == controlType) {
                        autoSelectedMotor = minMotor;
                    }
                } else {
                    item.price = null;
                    item.disabled = true;
                }
            });

        setAutoSelectedMotor(autoSelectedMotor);

        setTimeout(() => {
            if (autoSelectedMotor) {
                const selectedControlType =
                    controlType &&
                    document.querySelector(
                        `[type="radio"][id="radio-controlType-${controlType}"]:checked`
                    );
                selectedControlType && (selectedControlType.checked = false);

                const filteredControlType = controlTypes
                    .filter(item => item.motors)
                    .find(item =>
                        item.motors.find(
                            ({ sku }) => sku == autoSelectedMotor.sku
                        )
                    );

                const firstControlType = document.querySelector(
                    `[type="radio"][id^="radio-controlType-"][value="${
                        filteredControlType.value
                    }"]:enabled`
                );

                if (firstControlType) {
                    firstControlType.click();
                } else {
                    const checkedControlType = document.querySelector(
                        '[type="radio"][id^="radio-controlType-"]:checked'
                    );
                    checkedControlType && (checkedControlType.checked = false);
                }
            } else {
                const checkedControlType = document.querySelector(
                    '[type="radio"][id^="radio-controlType-"]:checked'
                );
                checkedControlType && (checkedControlType.checked = false);
            }
        }, 10);
    }, [
        lengthonly,
        width,
        height,
        nmMatrix,
        controlType,
        controlTypes,
        productDiscount
    ]);

    const [shouldShowSizeWarning, setShouldShowSizeWarning] = useState();
    const [shouldShowSizeAlert, setShouldShowSizeAlert] = useState();
    const [sizePrice, setSizePrice] = useState();

    useEffect(() => {
        if (lengthonly) {
            setSizePrice(width * buying);
        } else if (useMatrixPrice) {
            const matrixPrice = getMatrixPrice(width, height);
            console.log('Matrix price', width, height, matrixPrice);
            setSizePrice(matrixPrice);
        } else {
            let size = totalSize / 10000;

            if (size < minsize) {
                size = minsize;
            }

            setSizePrice(size * buying);
        }
    }, [lengthonly, width, buying, useMatrixPrice, totalSize]);

    useEffect(() => {
        const should = (maxsize && totalSize > maxsize) || isNaN(sizePrice);
        setShouldShowSizeWarning(should);
    }, [maxsize, totalSize, sizePrice]);

    useEffect(() => {
        const should =
            (sizeWidthMax && width > sizeWidthMax * 1) ||
            (sizeWidthMin && width < sizeWidthMin * 1) ||
            (sizeHeightMax && height > sizeHeightMax * 1) ||
            (sizeHeightMin && height < sizeHeightMin * 1);

        setShouldShowSizeAlert(should);
    }, [
        sizeWidthMax,
        sizeWidthMin,
        sizeHeightMax,
        sizeHeightMin,
        width,
        height
    ]);

    // bind size warning alert
    const warrantyAlertBlock = useMemo(() => {
        let msg;
        let className = classes.alert;

        if (width && height) {
            if (shouldShowSizeAlert) {
                msg = _(
                    'This size is not available. Please contact us to analyze the possible options.'
                );
                className = classes.alert;
            } else if (shouldShowSizeWarning) {
                msg = _(
                    'This size is exceeding normal sizes. We recommend to contact support first before ordering.'
                );
                className = classes.warning;
            } else {
                msg = null;
            }
        } else if (isFormSubmited) {
            msg = _('Please complete required fields');
        }
        return msg ? <div className={className}>{msg}</div> : null;
    }, [
        width,
        height,
        isFormSubmited,
        shouldShowSizeAlert,
        shouldShowSizeWarning
    ]);

    useEffect(() => {
        if (caseModel) {
            const caseModelObject = caseModels.items.find(
                ({ code }) => code == caseModel
            );

            const dimensionsValidForCurrentCaseModel =
                parseFloat(width) >= parseFloat(caseModelObject.min_width) &&
                parseFloat(width) <= parseFloat(caseModelObject.max_width) &&
                parseFloat(height) >= parseFloat(caseModelObject.min_height) &&
                parseFloat(height) <= parseFloat(caseModelObject.max_height);

            const validPrice = !!getMatrixValue(
                pricesMatrix[caseModel],
                width,
                height
            );

            if (dimensionsValidForCurrentCaseModel && validPrice) {
                return;
            }
        }

        const sortedCaseModels = [
            ...caseModels.items.sort((a, b) => a.max_width - b.max_width)
        ];

        let smallestCase;

        do {
            smallestCase = sortedCaseModels.shift();

            if (!smallestCase) {
                break;
            }

            if (
                getMatrixValue(pricesMatrix[smallestCase.code], width, height) >
                    0 &&
                parseFloat(width) < parseFloat(smallestCase.max_width) &&
                parseFloat(height) < parseFloat(smallestCase.max_height)
            ) {
                break;
            }
        } while (true);

        if (smallestCase && smallestCase.code != caseModel) {
            console.log('auto selected smallest case', caseModel, smallestCase);
            setCaseModel(smallestCase.code);
            document
                .getElementById(`radio-caseModel-${smallestCase.code}`)
                .click();
        }
    }, [width, height, caseModels, caseModel]);

    // when height changes, recalculate cast height
    configurator.kase &&
        useEffect(() => {
            let caseHeight;

            const { matrix, sizes } = configurator.kase;
            if (matrix) {
                caseHeight = getMatrixValue(sizes, width, height);
            } else {
                const firstKey = Object.keys(sizes).filter(
                    caseHeight => parseInt(caseHeight) > parseFloat(height)
                )[0];

                caseHeight = sizes[firstKey];
            }
            setCaseHeight(caseHeight);
        }, [height, width]);

    function getMatrixPrice(width, height) {
        const pricesTable = pricesMatrix[caseModel] || pricesMatrix;
        console.log('PRICE MATRIX', pricesTable);
        const matrixValue = getMatrixValue(pricesTable, width, height);
        return parseFloat(matrixValue);
    }

    function getMatrixValue(matrix, width, height) {
        // we need width and height as numbers for correct calculations
        width = parseInt(width);
        height = parseInt(height);

        if (!width || !height) {
            return null;
        }

        if (!matrix['w']) {
            alert('Wrong price matrix');
            return null;
        }

        let nextWidth = null;
        let nextHeight = null;

        for (let i = 0; i < matrix['w'].length; i++) {
            if (
                parseFloat(matrix['w'][i]) >= width &&
                (!sizeWidth.min || width >= sizeWidth.min)
            ) {
                nextWidth = i - 1;
                break;
            }
        }

        for (const i in matrix) {
            if (
                i >= height &&
                (!sizeHeightMin || height >= sizeHeightMin) &&
                i !== 'w'
            ) {
                nextHeight = i;
                break;
            }
        }

        const value = matrix[nextHeight] ? matrix[nextHeight][nextWidth] : null;

        return value;
    }

    function getColorsPrice(basePrice) {
        return Object.keys(colorLists).reduce((accumulator, key) => {
            // Safety check: ensure colorLists[key] exists
            if (!colorLists[key] || !colorLists[key].items) {
                return accumulator;
            }
            
            const caseKey = caseType ? 'kast_type-' + caseType : null;

            const colorListItems =
                (caseKey && colorLists[key].items[caseKey]) ||
                colorLists[key].items;

            const currentColor =
                typeof colors.get(key) == 'object'
                    ? colors.get(key)
                    : (colorListItems &&
                          colorListItems.find &&
                          colorListItems.find(
                              item => item.code == [colors.get(key)]
                          )) ||
                      {};

            const { price: currentColorPrice = 0, priceBy } = currentColor;
            let tmpPrice;

            if (!currentColorPrice) {
                return accumulator;
            }

            if (currentColorPrice.substr(-1) === '%') {
                tmpPrice = parseFloat(currentColorPrice);
                tmpPrice = (basePrice * tmpPrice) / 100;
            } else {
                tmpPrice = parseFloat(currentColorPrice);
            }

            if (priceBy) {
                if (priceBy === 'width') {
                    tmpPrice = (width / 100) * tmpPrice;
                } else if (priceBy === 'height') {
                    tmpPrice = (height / 100) * tmpPrice;
                } else {
                    console.error('Unknown "price by"', priceBy);
                }
            }

            return accumulator + tmpPrice;
        }, 0);
    }

    function getExtrasPrice(basePrice) {
        let extras = [];

        const selectedCaseType =
            caseTypeItems.find &&
            caseTypeItems.find(item => item.code == caseType);

        if (selectedCaseType) {
            extras.push(selectedCaseType);
        }

        if (controlSide && controlPositions) {
            const selectedControlPosition = controlPositions.find(
                item => item.key == controlSide
            );
            selectedControlPosition && extras.push(selectedControlPosition);
        }

        if (montageItems && montageItems.length) {
            montages.forEach((value, key) => {
                const montageCategory = montageItems.find(item => item.key == key);
                if (!montageCategory || !montageCategory.items) {
                    return;
                }
                
                const montageItem = montageCategory.items.find(item => item.key == value);

                if (!montageItem) {
                    return;
                }

                extras.push(montageItem);
            });
        }

        const currentControlType = controlTypes.filter(
            type => type.value == controlType
        )[0];

        const currentControlProduct =
            currentControlType && currentControlType.items
                ? currentControlType.items.filter(
                      item => item.value == controlProduct
                  )[0]
                : null;

        extras = extras.concat(currentControlType, currentControlProduct);

        console.log(extras);
        //debugger

        const extrasPrice = extras
            .filter(extra => extra && parseFloat(extra.price))
            .map(function(extra) {
                // cast to string so we can safely check for percentages (ex: "2.42%")
                let extraPrice = extra.price + '';

                if (!extraPrice) {
                    return;
                }

                if (extraPrice.substr(-1) === '%') {
                    extraPrice = parseFloat(extraPrice);
                    extraPrice = (basePrice * extraPrice) / 100;
                } else {
                    extraPrice = parseFloat(extraPrice);
                }

                const priceBy = extra.price_by;

                if (priceBy) {
                    if (priceBy === 'width') {
                        extraPrice = (width / 100) * extraPrice;
                    } else if (priceBy === 'height') {
                        extraPrice = (height / 100) * extraPrice;
                    } else {
                        console.error('Unknown "price by"', priceBy, extra);
                    }
                }

                return extraPrice;
            })
            .reduce((a, b) => a + b, 0);

        return extrasPrice;
    }

    // recalculate price when there's a price-related property changes
    const prevPriceRef = React.useRef({ finalPriceInclTax: null, advicedPriceInclTax: null });
    
    useEffect(() => {
        const priceSize = sizePrice;
        const priceColors = getColorsPrice(priceSize);
        const priceExtras = getExtrasPrice(priceSize);

        /** formula for DonPersiana
        let finalPrice =
            (configurator.setup + priceSize + priceColors) *
                (1 + configurator.rate / 100) +
            priceExtras;
            */

        let finalPrice = priceSize + priceColors + priceExtras;

        console.log(
            `Configurator prices. size=${priceSize} colors=${priceColors} extras=${priceExtras} productDiscount=${productDiscount}`
        );

        // true if price_size is null, undefined or 0
        if (!priceSize) {
            finalPrice = '-';
        }

        if (finalPrice >= 0) {
            // round finalPrice
            finalPrice = Math.round(100 * finalPrice) / 100;

            const taxpercent = 0 * configurator.tax;

            let finalPriceInclTax = taxpercent
                ? finalPrice * taxpercent
                : finalPrice;
            const advicedPrice = finalPrice * configurator.advicedPrice;
            let advicedPriceInclTax = taxpercent
                ? advicedPrice * taxpercent
                : advicedPrice;

            if (productDiscount) {
                console.log('FINAL PRICE', finalPrice);
                advicedPriceInclTax = finalPriceInclTax;
                finalPriceInclTax -=
                    finalPriceInclTax / (100 / productDiscount);
            } else {
                advicedPriceInclTax = null;
            }
            console.log(
                'FINAL PRICE ADVICED',
                advicedPriceInclTax,
                productDiscount
            );
            // round prices
            finalPriceInclTax = Math.round(100 * finalPriceInclTax) / 100;
            advicedPriceInclTax = Math.round(100 * advicedPriceInclTax) / 100;

            // Only call callbacks if prices actually changed (prevent infinite loop)
            if (prevPriceRef.current.finalPriceInclTax !== finalPriceInclTax ||
                prevPriceRef.current.advicedPriceInclTax !== advicedPriceInclTax) {
                prevPriceRef.current = { finalPriceInclTax, advicedPriceInclTax };
                onPriceChange(finalPriceInclTax);
                onAdvicedPriceChange(advicedPriceInclTax);
            }

            setIsConfiguratorValid(true);
        } else {
            // Only call callbacks if values changed
            if (prevPriceRef.current.finalPriceInclTax !== null) {
                prevPriceRef.current = { finalPriceInclTax: null, advicedPriceInclTax: null };
                onPriceChange(null);
                onAdvicedPriceChange(null);
            }

            setIsConfiguratorValid(false);
        }

        console.log(
            'Prices size/colors/extras/final',
            priceSize,
            priceColors,
            priceExtras,
            finalPrice
        );
    }, [
        width,
        height,
        colors,
        montages,
        caseModel,
        caseType,
        controlType,
        controlSide,
        controlProduct,
        isLoaded,
        sizePrice,
        productDiscount
    ]);

    const sizeInfo = help.yd_meet_instr_txt;
    const colorInfo = help.yd_info_kleur;
    const montageInfo = help.yd_info_mont_txt;
    const caseInfo = help.yd_info_kast_txt;
    const controlInfo = help.yd_info_bedien_txt;

    const [isInfoOpen, setIsInfoOpen] = useState(false);

    const showInfo = e => {
        e.preventDefault && e.preventDefault();
        setIsInfoOpen(true);
    };

    const hideInfo = e => {
        e.preventDefault && e.preventDefault();
        setIsInfoOpen(false);
    };

    useEffect(() => {
        console.log('MONTAGE', montageItems);
        if (montageItems && montageItems.length) {
            montageItems
                .filter(item => item.size_matrix)
                .forEach(montageItem => {
                    const autoMontageItemCode = getMatrixValue(
                        montageItem.size_matrix,
                        width,
                        height
                    );

                    console.log('MONTAGE AUTO SELECTED', autoMontageItemCode);

                    if (autoMontageItemCode) {
                        const element = document.querySelector(
                            `[value="${autoMontageItemCode.toLowerCase()}"]`
                        );

                        if (!element) {
                            alert(
                                `Cannot autoselect montage item with code ${autoMontageItemCode.toLowerCase()}`
                            );
                            return false;
                        }

                        const isDisabled = element.disabled;
                        if (isDisabled) {
                            element.disabled = false;
                        }
                        element.click();
                        if (isDisabled) {
                            element.disabled = true;
                        }
                    }
                    console.log('MONTAGE AUTO', autoMontageItemCode);
                });
        }
    }, [montageItems, width, height]);

    // sections are disabled if no values are entered in both dimensions (or width
    // for length only products)
    const sectionsDisabled = useMemo(() => {
        return lengthonly
            ? !width || width.length === 0
            : !width || !height || width.length === 0 || height.length === 0;
    }, [width, height, lengthonly]);

    useEffect(() => {
        setIsLoaded(true);

        // will trigger price calculation and product initial state
        const timer = setTimeout(() => {
            setIsLoaded(new Date() - 0);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    const deepLink = useMemo(() => {
        const params = [
            ['width', width],
            ['height', height],
            ['montages', JSON.stringify([...montages])],
            ['caseModel', caseModel],
            ['caseType', caseType],
            ['colors', JSON.stringify([...colors])],
            ['controlType', controlType],
            ['controlSide', controlSide],
            ['controlProduct', controlProduct]
        ].filter(([, value]) => value !== undefined && value !== null);

        const searchParams = new URLSearchParams(params);

        if (process.env.NODE_ENV == 'development') {
            return (
                product.canonical_url.replace(
                    /https:\/\/.*?\//,
                    'https://dev.lightr.es/'
                ) +
                '?' +
                searchParams
            );
        }

        return product.canonical_url + '?' + searchParams;
    }, [
        product,
        width,
        height,
        colors,
        montages,
        caseModel,
        caseType,
        controlType,
        controlSide,
        controlProduct
    ]);

    useEffect(() => {
        console.log('configured', deepLink);
        // Note: deepLink mutation removed to prevent infinite re-render loop
        // Don't mutate product object directly
    }, [deepLink]);

    const className = lengthonly ? classes.rootLengthOnly : classes.root;

    return (
        <Fragment>
            <div className={className}>
                {cannotAddToCartAlert}
                <textarea
                    value={configuratorOptions}
                    name="options[1]"
                    readOnly
                    rows="12"
                />
                {sizeInfo ? (
                    <Dialog
                        isOpen={isInfoOpen}
                        onConfirm={hideInfo}
                        onCancel={hideInfo}
                        showCancelButton={false}
                        confirmText={_('Close')}
                        title={sizeInfo.title}
                    >
                        <RichContent html={sizeInfo.text} />
                    </Dialog>
                ) : null}

                <div className={classes.sellbox}>
                    <div className={classes.configproductheader}>
                        <div>
                            <h1>
                                {product.name}
                                <span className={classes.promoseal} />
                            </h1>

                            <span className={classes.promotext}>
                                {_('Pide directo online y ahorra mucho dinero')}
                            </span>
                            <span className={classes.subline}>
                                {_('Start configurating')}
                                {/* {_('Calculate your budget')} */}
                            </span>
                        </div>

                        <div className={classes.pricecalculator}>
                            {priceBlock}
                        </div>
                    </div>

                    {/* <span className={classes.shipping}>
                        {' '}
                        {_('Delivery in 3 working days.')}
                    </span> */}
                    {/* <h3>{_('Start configurating')}</h3> */}

                    {!printWizardElements ? null : useWizard ? (
                        <>
                            <button
                                className={classes.wizardSwitch}
                                onClick={() => setUseWizard(false)}
                            >
                                <ToolIcon width={12} />
                                {_('Configure using one page view')}
                            </button>
                            <ConfiguratorWizard
                                steps={steps}
                                selectedIndex={activeStep.index}
                                onSelect={index => setActiveStep(steps[index])}
                            />
                        </>
                    ) : (
                        <button
                            className={classes.wizardSwitch}
                            onClick={() => setUseWizard(true)}
                        >
                            <CompassIcon width={12} />
                            {_('Configure using the wizard')}
                        </button>
                    )}
                </div>

                <div
                    className={classes.stepContainer}
                    hidden={useWizard && activeStep.code != 'dimensions'}
                >
                    <fieldset
                        className={classes.sizessection}
                        code="dimensions"
                    >
                        <legend>
                            <span className={classes.grouptitle}>
                                {_('Medidas')}
                            </span>
                            <span
                                onClick={showInfo}
                                className={classes.info}
                                title={_('Instrucciones para medir')}
                            >
                                👁 {_('Read')} {_('Instrucciones para medir')}
                            </span>
                            <span className={classes.subtitle}>
                                {_('Introduce las medidas')}
                            </span>
                        </legend>
                        <div className={classes.sizeholder}>
                            <div className={classes.item}>
                                <div className={classes.holder}>
                                    <span className={classes.title}>
                                        {lengthonly
                                            ? _(
                                                  'Length in {0}',
                                                  sizeWidth.units
                                              )
                                            : _(
                                                  hasCaseTypes
                                                      ? 'Ancho en {0}'
                                                      : 'Ancho ({0})',
                                                  sizeWidth.units
                                              )}
                                    </span>
                                </div>

                                <div className={classes.holder}>
                                    <span className={classes.textfield}>
                                        <TextInput
                                            type="number"
                                            inputMode="decimal"
                                            min={sizeWidthMin}
                                            max={sizeWidthMax}
                                            step={
                                                (sizeWidth && sizeWidth.step) ||
                                                0.1
                                            }
                                            initialValue={width}
                                            value={width}
                                            placeholder={_('ej. 100.0')}
                                            defaultValue={defaultWidth}
                                            onInput={e => {
                                                setWidth(e.target.value);
                                                if (e.target.value && height) {
                                                    e.target
                                                        .closest('fieldset')
                                                        .classList.remove(
                                                            'invalid'
                                                        );
                                                    setIsFormSubmited(false);
                                                }
                                            }}
                                            required
                                            field="min"
                                        />
                                    </span>
                                </div>
                                <span className={classes.infoline}>
                                    ({_('min:')} {sizeWidthMin}{' '}
                                    {sizeWidth && sizeWidth.units}, {_('max:')}{' '}
                                    {sizeWidthMaxRange}{' '}
                                    {sizeWidth && sizeWidth.units})
                                </span>
                            </div>

                            {lengthonly ? null : (
                                <div className={classes.item}>
                                    <div className={classes.holder}>
                                        <span className={classes.title}>
                                            {_(
                                                hasCaseTypes
                                                    ? 'Altura en {0}'
                                                    : 'Altura ({0})',
                                                sizeHeight.units
                                            )}
                                        </span>
                                    </div>
                                    <div className={classes.holder}>
                                        <span className={classes.textfield}>
                                            <TextInput
                                                type="number"
                                                inputMode="decimal"
                                                min={sizeHeightMin}
                                                max={sizeHeightMax}
                                                step={
                                                    (sizeHeight &&
                                                        sizeHeight.step) ||
                                                    0.1
                                                }
                                                initialValue={height}
                                                value={height}
                                                placeholder={_('ej. 100.0')}
                                                defaultValue={defaultHeight}
                                                onInput={e => {
                                                    setHeight(e.target.value);
                                                    if (
                                                        e.target.value &&
                                                        width
                                                    ) {
                                                        e.target
                                                            .closest('fieldset')
                                                            .classList.remove(
                                                                'invalid'
                                                            );
                                                        setIsFormSubmited(
                                                            false
                                                        );
                                                    }
                                                }}
                                                field="max"
                                            />
                                        </span>
                                    </div>
                                    <span className={classes.infoline}>
                                        <span className={classes.blockline}>
                                            ({_('min:')} {sizeHeightMin}{' '}
                                            {sizeHeight && sizeHeight.units},{' '}
                                            {_('max:')} {sizeHeightMaxRange}{' '}
                                            {sizeHeight && sizeHeight.units})
                                        </span>

                                        {caseHeight &&
                                            ' ' +
                                                _(
                                                    'Altura incluyendo el cajón {0} {1}',
                                                    new Number(
                                                        caseHeight
                                                    ).toLocaleString(),
                                                    sizeHeight.units
                                                )}
                                    </span>
                                </div>
                            )}
                        </div>
                        {warrantyAlertBlock}
                    </fieldset>
                </div>

                {montageItems.length ? (
                    <div hidden={useWizard && activeStep.code != 'montage'}>
                        <MontageItems
                            items={montageItems}
                            classes={classes}
                            onChange={setSelectedMontage}
                            info={montageInfo}
                            sectionDisabled={sectionsDisabled}
                        />
                    </div>
                ) : null}

                {caseTypes.length || Object.keys(caseTypes).length ? (
                    <div hidden={useWizard && activeStep.code != 'case'}>
                        <CaseTypes
                            items={caseTypeItems}
                            models={caseModels && caseModels.items}
                            classes={classes}
                            selectedModel={caseModel}
                            selectedType={caseType}
                            onCaseModelChange={setCaseModel}
                            onCaseTypeChange={setCaseType}
                            info={caseInfo}
                            sectionDisabled={sectionsDisabled}
                        />
                    </div>
                ) : null}

                <div hidden={useWizard && activeStep.code != 'color'}>
                    <ColorList
                        colors={colorLists}
                        ral={colors_ral}
                        classes={classes}
                        selected={colors}
                        selectedCase={caseType}
                        onChange={setSelectedColor}
                        info={colorInfo}
                        sectionDisabled={sectionsDisabled}
                        productDiscount={productDiscount}
                    />
                </div>

                {controlTypes.length ? (
                    <div hidden={useWizard && activeStep.code != 'control'}>
                        <ControlTypes
                            items={controlTypes}
                            controlPositions={
                                controlPositions
                            } /* https://youngdogs.atlassian.net/browse/ZNSVR-555 */
                            classes={classes}
                            onChangeSide={setControlSide}
                            onChangeType={setControlType}
                            onChangeProduct={setControlProduct}
                            info={controlInfo}
                            sectionDisabled={sectionsDisabled}
                        />
                    </div>
                ) : null}

                {lengthonly ? (
                    addToCartBlock
                ) : (
                    <div hidden={useWizard && activeStep.code != 'summary'}>
                        <legend>{_('Summary')}</legend>
                        <div className={classes.resumen}>
                            {/*<div className={classes.summary}>
                                {lengthonly ? null : (
                                    <div className={classes.render}>
                                        <div
                                            className={classes.cvalue}
                                            data-type="width"
                                        >
                                            {width}
                                        </div>
                                        <div
                                            className={classes.cvalue}
                                            data-type="height"
                                        >
                                            {height}
                                        </div>
                                        <div
                                            className={classes.cvalue}
                                            data-type="guiderleft"
                                        >
                                            {guiderLeftSize}
                                        </div>
                                        <div
                                            className={classes.cvalue}
                                            data-type="guiderright"
                                        >
                                            {guiderRightSize}
                                        </div>
                                        <div
                                            className={classes.cvalue}
                                            data-type="caseheight"
                                        >
                                            {caseHeight}
                                        </div>
                                    </div>
                                )}
                            <span className={classes.overview}>
                                {_('Overview')}{' '}
                            </span>
                                </div> */}
                            <div className={classes.resumenlist}>
                                <ProductOptions
                                    options={configuratorOptionsToProductOptions(
                                        configuratorOptions
                                    )}
                                />
                            </div>
                        </div>
                        {addToCartBlock}
                    </div>
                )}

                <div className={classes.navigateContainer}>
                    <ConfiguratorShare
                        deepLink={deepLink}
                        product={product}
                        configuratorOptions={configuratorOptions}
                    />
                    {useWizard && (
                        <>
                            {prevStep && (
                                <button
                                    className={`${classes.navigateButton} ${
                                        classes.navigatePrev
                                    }`}
                                    onClick={goToPrevStep}
                                >
                                    <span
                                        className={
                                            classes.navigateBackButtonLabel
                                        }
                                        aria-hidden={true}
                                    >
                                        {_('Back to:')}
                                        {/* {prevStep.label} */}
                                    </span>
                                </button>
                            )}

                            {nextStep && (
                                <button
                                    className={`${classes.navigateButton} ${
                                        classes.navigateNext
                                    }`}
                                    onClick={goToNextStep}
                                    hidden={!nextStep}
                                >
                                    <span
                                        className={classes.navigateButtonLabel}
                                    >
                                        {_('Volgende stap: ')}
                                        <strong>{nextStep.label}</strong>
                                        {/* @todo can you please add a scroll back to top here? */}
                                    </span>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Fragment>
    );
};
/*
ConfiguratorSection.propTypes = {
    clases: shape({
        root: string,
        icon: string
    }),
    title: string,
    text: string,
    buttonTitle: string,
    buttonText: string
};

ConfiguratorSection.defaultProps = {
    buttonTitle: _('Share this element'),
    buttonText: _('Share this!'),
};
*/
export default ConfiguratorSection;
